<template>
  <section>
    <div
      class="main-div cursor-pointer"
      :class="{ 'cursor-no-drop': !canUploadMoreFiles }"
      ref="fileDropZone"
      @mousedown="reset()"
      @click="canUploadMoreFiles ? open() : null"
      @keyup.enter="canUploadMoreFiles ? open() : null"
      :style="{ backgroundColor: backgroundColor[status] }"
      v-if="!hideDropZone"
    >
      <v-row>
        <v-col cols="12" class="justify-center py-0 d-flex upload-icon">
          <slot name="icon" :props="slotProps">
            <v-icon
              size="200"
              color="#A4A4A4"
              :class="{ 'icon-translate': isOverDropZone }"
            >
              {{ icon[status] }}
            </v-icon>
          </slot>
        </v-col>

        <v-col cols="12" class="justify-center py-0 d-flex fill-width">
          <slot name="title" :props="slotProps">
            <p
              class="upload-title-1"
              aria-live="assertive"
              v-html="title[status]"
              tabindex="0"
            />
          </slot>
        </v-col>
      </v-row>
    </div>

    <v-row class="pt-3">
      <slot name="files-prepend" />

      <slot name="files" :props="slotProps">
        <v-col
          :cols="12"
          v-for="(file, index) in [...data, ...uploadedFiles]"
          :key="'sds-file-upload-file-' + index"
          class="py-1"
        >
          <slot name="file" :props="slotProps" :file="file" :index="index">
            <SdsFileCard
              :name="file.name"
              :last-modified="getFormattedDate(file.lastModified)"
              @click:remove-file="removeFile(file)"
              :identifier="'identifier' in file ? file.identifier : index"
              :download-link="'downloadLink' in file ? file.downloadLink : ''"
              :hide-download="
                'hideDownload' in file ? file.hideDownload : hideDownloadButton
              "
              :hide-remove="'hideRemove' in file ? file.hideRemove : false"
            />
          </slot>
        </v-col>
      </slot>

      <slot name="files-append" />
    </v-row>
  </section>
</template>

<script setup lang="ts">
import {
  mdiCloudAlert,
  mdiCloudCheck,
  mdiCloudDownload,
  mdiCloudUpload,
} from '@mdi/js';
import { WritableComputedRef, computed, ref, withDefaults } from 'vue';
import { useDropZone, useFileDialog, useVModel } from '@vueuse/core';
import { TranslateResult } from 'vue-i18n';
import {
  getFileExtension,
  getFormattedDate,
  isAcceptedFileType,
} from './utils';
import SdsFileCard from './SdsFileCard.vue';
import { UploadedFile } from './types';

enum Status {
  DEFAULT = 'default',
  WARNING = 'warning',
  SUCCESS = 'success',
  INFO = 'info',
}

type Props = {
  value: File[];
  /**
   * Maximum number of files that can be uploaded.
   * @default 10
   */
  maxFiles?: number;
  /**
   * Hide the download button on each file.
   * @default false
   */
  hideDownloadButton?: boolean;
  /**
   * Accepted file types.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#accept
   * @default '*'
   */
  accept?: string;
  /**
   * Message displayed when the component is at the default state.
   */
  messageTitle?: string | TranslateResult;
  /**
   * Message displayed when the component is at the max number of files.
   */
  messageSuccess?: string | TranslateResult;
  /**
   * Message displayed when the component is at the max number of files and the user tries to attach more.
   */
  messageLimitExceeded?: string | TranslateResult;
  /**
   * Message displayed when the user is dragging a file over the component.
   */
  messageDropHere?: string | TranslateResult;
  /**
   * Message displayed when the user tries to upload a file with an extension that is not accepted.
   * @example "Extension "<b>{extension}</b>" not accepted."
   */
  messageExtensionNotAccepted?: string | TranslateResult;
  /**
   * Message displayed when the user tries to upload a file that is already selected.
   * @example "File "{file}" already selected."
   */
  messageFileAlreadySelected?: string | TranslateResult;
  /**
   * Files that have been already uploaded
   * @default []
   */
  uploadedFiles?: UploadedFile[];
  /**
   * Hides the upload drop zone.
   * @default false
   */
  hideDropZone?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  value: () => [],
  maxFiles: 10,
  hideDownloadButton: false,
  accept: '*',
  messageSuccess: 'Arquivo(s) selecionado(s) com sucesso.',
  messageLimitExceeded: 'Limite de arquivos excedido.',
  messageDropHere: 'Solte o arquivo para anexar',
  messageTitle:
    '<div style="text-align: center; font-size: 14px; color: black"> Arraste e solte o arquivo <br> Ou <span style="color: #00579B; font-weight: bold"> clique aqui </span> para anexar</div>',
  messageExtensionNotAccepted: 'A extensão "<b>{extension}</b>" não é aceita.',
  messageFileAlreadySelected: 'Arquivo "{file}" já selecionado.',
  uploadedFiles: () => [],
});

// v-model
const emit = defineEmits(['input', 'click:remove-uploaded-file']);
const data = useVModel(props, undefined, emit) as WritableComputedRef<File[]>;

// file-drop-zone
const fileDropZone = ref<HTMLDivElement>();
const { isOverDropZone } = useDropZone(fileDropZone, {
  onDrop,
});
function onDrop(files: File[] | null) {
  if (!files) {
    return;
  }

  for (const file of files) {
    addFile(file);
  }
}

// clickable file dialog
const { open, reset, onChange } = useFileDialog({
  accept: props.accept,
  multiple: props.maxFiles > 1,
});
onChange((files: FileList | null) => {
  if (!files) {
    return;
  }

  for (const file of files) {
    addFile(file);
  }
});

// forced warning
const forcedWarning = ref<string | TranslateResult>('');

// basic functions
function addFile(file: File) {
  if (!canUploadMoreFiles.value) {
    return;
  }

  if (!isAcceptedFileType(file, props.accept)) {
    const extension = `.${getFileExtension(file)}`;
    showWarning(
      String(props.messageExtensionNotAccepted).replace(
        '{extension}',
        extension
      )
    );
    return;
  }

  const value = data.value || [];

  const fileAlreadyExists = value.some(
    (existingFile) =>
      existingFile.name === file.name &&
      existingFile.lastModified === file.lastModified
  );

  if (fileAlreadyExists) {
    const message = String(props.messageFileAlreadySelected).replace(
      '{file}',
      file.name
    );
    showWarning(message);
    return;
  }

  value.push(file);

  data.value = value;
}
function removeFile(file: File | UploadedFile) {
  if (file instanceof File) {
    const value = data.value || [];

    const index = value.findIndex(
      (existingFile) =>
        existingFile.name === file.name &&
        existingFile.lastModified === file.lastModified
    );

    value.splice(index, 1);

    data.value = value;
    return;
  }

  emit('click:remove-uploaded-file', file);
}
function showWarning(message: string | TranslateResult) {
  forcedWarning.value = message;

  setTimeout(() => {
    forcedWarning.value = '';
  }, 2000);
}

// main properties
const status = computed(() => {
  if (
    (forcedWarning.value && !isOverDropZone.value) ||
    (!canUploadMoreFiles.value && isOverDropZone.value)
  ) {
    return Status.WARNING;
  }

  if (!canUploadMoreFiles.value) {
    return Status.SUCCESS;
  }

  if (isOverDropZone.value) {
    return Status.INFO;
  }

  return Status.DEFAULT;
});
const canUploadMoreFiles = computed(() => {
  return data.value.length + props.uploadedFiles.length < props.maxFiles;
});
const slotProps = computed(() => ({
  isOverDropZone: isOverDropZone.value,
  status: status.value,
  title: title.value[status.value],
  icon: icon.value[status.value],
  canUploadMoreFiles: canUploadMoreFiles.value,
  forcedWarning: forcedWarning.value,
}));

// component visual properties
const title = computed(() => ({
  [Status.WARNING]: forcedWarning.value || props.messageLimitExceeded,
  [Status.SUCCESS]: props.messageSuccess,
  [Status.INFO]: props.messageDropHere,
  [Status.DEFAULT]: props.messageTitle,
}));
const icon = computed(() => ({
  [Status.WARNING]: mdiCloudAlert,
  [Status.SUCCESS]: mdiCloudCheck,
  [Status.INFO]: mdiCloudDownload,
  [Status.DEFAULT]: mdiCloudUpload,
}));
const backgroundColor = computed(() => ({
  [Status.WARNING]: '#fffcf2',
  [Status.SUCCESS]: '#f5fcf5',
  [Status.INFO]: 'rgb(0,0,0,0.05)',
  [Status.DEFAULT]: 'transparent',
}));
</script>

<style scoped>
.main-div {
  border: 1px dashed #a4a4a4;
  min-height: 250px;
  transition: 250ms all;
}

.upload-title-1 {
  color: #666666;
  font-size: 16px;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-no-drop {
  cursor: no-drop !important;
}

.upload-icon {
  transition: 250ms all;
}

.icon-translate {
  transform: translateY(-10px);
}
</style>
